// Simple in-memory store for buying agents (resets on page refresh)
// This allows dynamic creation of agents from other pages

export interface AnalysisData {
  vendorsEvaluated?: number;
  criteriaUsed?: string[];
  topCandidates?: string[];
  budgetCheck?: string;
  inventoryCheck?: string;
  vendorsBeingEvaluated?: number;
  checkingInventory?: boolean;
  checkingPreferredSuppliers?: boolean;
  specialRequirements?: string;
}

export interface NegotiationItem {
  supplier: string;
  originalPrice?: number;
  negotiatedPrice?: number;
  discount: number;
}

export interface QuoteItem {
  supplier: string;
  quote?: number;
  overBudget?: string;
  leadTime?: string;
  issue?: string;
}

export interface EmailMessage {
  id: string;
  from: string;
  to: string;
  sentAt: string;
  body: string;
  attachments?: string[];
  isReply?: boolean;
}

export interface CallRecording {
  duration: string;
  recordingUrl: string;
  summary: string;
  transcript?: string;
}

export interface ActiveCommunication {
  id: string;
  supplier: string;
  type: 'email' | 'call' | 'meeting';
  status:
    | 'sent'
    | 'in_progress'
    | 'awaiting_response'
    | 'responded'
    | 'completed';
  subject?: string;
  sentAt?: string;
  lastActivity?: string;
  // Summary info for display
  summary?: string;
  quotedPrice?: number;
  quotedLeadTime?: string;
  volumeDiscountOffer?: {
    product: string;
    sku: string;
    discountPercent: number;
    minQuantity: number;
  };
  emailContent?: {
    to: string;
    from: string;
    body: string;
    attachments?: string[];
  };
  emailThread?: EmailMessage[];
  // For calls
  callRecording?: CallRecording;
  // Supplier phone number for follow-up calls
  supplierPhone?: string;
}

export interface CommunicationData {
  rfqsSent?: number;
  responsesReceived?: number;
  negotiations?: NegotiationItem[];
  selectedSupplier?: string;
  confirmedLeadTime?: number;
  awaitingResponse?: string[];
  currentNegotiation?: string;
  negotiatedDiscount?: number;
  warrantyExtended?: string;
  failureReason?: string;
  quotesReceived?: QuoteItem[];
  activeCommunications?: ActiveCommunication[];
}

export interface ApprovalData {
  approver?: string;
  approverRole?: string;
  approvalThreshold?: string;
  requestedAt?: string;
  reminderSent?: boolean;
  approvedAt?: string;
  escalationReason?: string;
  recommendedSupplier?: string;
  recommendedPrice?: string;
  recommendation?: string;
  unitPrice?: number;
  totalPrice?: number;
  quantity?: number;
  leadTime?: string;
}

export interface GenerationData {
  poNumber?: string;
  sentTo?: string;
  totalValue?: number;
}

export type StepDetailsData =
  | AnalysisData
  | CommunicationData
  | ApprovalData
  | GenerationData;

export interface AgentStep {
  id: string;
  name: string;
  description: string;
  status: 'completed' | 'running' | 'pending' | 'failed';
  startedAt?: string;
  completedAt?: string;
  details?: {
    type: 'analysis' | 'communication' | 'approval' | 'generation';
    data: StepDetailsData;
  };
}

export interface AgentActivity {
  date: string;
  time: string;
  action: string;
  result: 'Success' | 'Pending Approval' | 'Action Required' | 'In Progress';
  savings?: number;
}

export interface VolumeDiscountItem {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  currentStock: number;
  potentialSavings: string;
  added?: boolean;
}

export interface BuyingAgent {
  id: string;
  requestNumber: string;
  product: string;
  productCategory: string;
  quantity: number;
  estimatedValue: number;
  status: 'running' | 'waiting_approval' | 'completed' | 'failed' | 'paused';
  currentStatusText: string;
  progress: number;
  startedAt: string;
  completedAt?: string;
  leadTimeDays: number;
  steps: AgentStep[];
  recentActivity: AgentActivity[];
  savings?: number;
  assignedApprover?: string;
  supplierSelected?: string;
  volumeDiscountItems?: VolumeDiscountItem[];
}

// Initial mock data
const initialBuyingAgents: BuyingAgent[] = [
  {
    id: 'agent-001',
    requestNumber: 'REQ-2026-00142',
    product: 'Steel Rods (Grade A)',
    productCategory: 'Raw Materials',
    quantity: 500,
    estimatedValue: 12500,
    status: 'waiting_approval',
    currentStatusText: 'Waiting for manager approval',
    progress: 75,
    startedAt: '2026-01-21T14:30:00Z',
    leadTimeDays: 14,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Requirements',
        description:
          'Analyzing purchase requirements and identifying potential suppliers',
        status: 'completed',
        startedAt: '2026-01-21T14:30:00Z',
        completedAt: '2026-01-21T14:32:00Z',
        details: {
          type: 'analysis',
          data: {
            vendorsEvaluated: 8,
            criteriaUsed: ['price', 'quality', 'lead_time', 'reliability'],
            topCandidates: ['MetalWorks Inc', 'SteelCo', 'Industrial Metals'],
            budgetCheck: 'passed',
            inventoryCheck: 'low_stock_confirmed',
          },
        },
      },
      {
        id: 'communicate',
        name: 'Communicate with Suppliers',
        description:
          'Sending RFQs, negotiating prices, and confirming delivery timelines',
        status: 'completed',
        startedAt: '2026-01-21T14:32:00Z',
        completedAt: '2026-01-21T14:45:00Z',
        details: {
          type: 'communication',
          data: {
            rfqsSent: 5,
            responsesReceived: 4,
            negotiations: [
              {
                supplier: 'MetalWorks Inc',
                originalPrice: 14200,
                negotiatedPrice: 12500,
                discount: 12,
              },
              {
                supplier: 'SteelCo',
                originalPrice: 13800,
                negotiatedPrice: 12800,
                discount: 7,
              },
            ],
            selectedSupplier: 'MetalWorks Inc',
            confirmedLeadTime: 14,
          },
        },
      },
      {
        id: 'approval',
        name: 'Human Approval',
        description: 'Awaiting approval from authorized personnel',
        status: 'running',
        startedAt: '2026-01-21T14:45:00Z',
        details: {
          type: 'approval',
          data: {
            approver: 'Michael Chen',
            approverRole: 'Procurement Manager',
            approvalThreshold: '$10,000',
            requestedAt: '2026-01-21T14:45:00Z',
            reminderSent: true,
          },
        },
      },
      {
        id: 'generate',
        name: 'Generate Purchase Order',
        description:
          'Creating and sending the purchase order to the selected supplier',
        status: 'pending',
        details: {
          type: 'generation',
          data: {},
        },
      },
    ],
    recentActivity: [
      {
        date: 'Jan 21, 2026',
        time: '2:45 PM',
        action: 'Sent approval request to Michael Chen',
        result: 'Pending Approval',
      },
      {
        date: 'Jan 21, 2026',
        time: '2:40 PM',
        action: 'Negotiated 12% discount with MetalWorks Inc',
        result: 'Success',
        savings: 1500,
      },
      {
        date: 'Jan 21, 2026',
        time: '2:35 PM',
        action: 'Received 4 quotes from suppliers',
        result: 'Success',
      },
      {
        date: 'Jan 21, 2026',
        time: '2:32 PM',
        action: 'Sent RFQ to 5 suppliers',
        result: 'Success',
      },
      {
        date: 'Jan 21, 2026',
        time: '2:30 PM',
        action: 'Evaluated 8 potential vendors',
        result: 'Success',
      },
    ],
    savings: 1700,
    assignedApprover: 'Michael Chen',
    supplierSelected: 'MetalWorks Inc',
  },
  {
    id: 'agent-002',
    requestNumber: 'REQ-2026-00143',
    product: 'Industrial Bearings',
    productCategory: 'Components',
    quantity: 200,
    estimatedValue: 8400,
    status: 'running',
    currentStatusText: 'Negotiating with suppliers',
    progress: 45,
    startedAt: '2026-01-21T15:00:00Z',
    leadTimeDays: 7,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Requirements',
        description:
          'Analyzing purchase requirements and identifying potential suppliers',
        status: 'completed',
        startedAt: '2026-01-21T15:00:00Z',
        completedAt: '2026-01-21T15:03:00Z',
        details: {
          type: 'analysis',
          data: {
            vendorsEvaluated: 6,
            criteriaUsed: ['price', 'quality', 'certification'],
            topCandidates: ['Precision Parts Co', 'BearingWorld', 'MechSupply'],
            budgetCheck: 'passed',
          },
        },
      },
      {
        id: 'communicate',
        name: 'Communicate with Suppliers',
        description:
          'Sending RFQs, negotiating prices, and confirming delivery timelines',
        status: 'running',
        startedAt: '2026-01-21T15:03:00Z',
        details: {
          type: 'communication',
          data: {
            rfqsSent: 4,
            responsesReceived: 2,
            awaitingResponse: ['BearingWorld', 'MechSupply'],
            currentNegotiation: 'Precision Parts Co - counter offer pending',
          },
        },
      },
      {
        id: 'approval',
        name: 'Human Approval',
        description: 'Awaiting approval from authorized personnel',
        status: 'pending',
      },
      {
        id: 'generate',
        name: 'Generate Purchase Order',
        description:
          'Creating and sending the purchase order to the selected supplier',
        status: 'pending',
      },
    ],
    recentActivity: [
      {
        date: 'Jan 21, 2026',
        time: '3:15 PM',
        action: 'Received counter-offer from Precision Parts Co',
        result: 'In Progress',
      },
      {
        date: 'Jan 21, 2026',
        time: '3:05 PM',
        action: 'Sent RFQ to 4 suppliers',
        result: 'Success',
      },
      {
        date: 'Jan 21, 2026',
        time: '3:03 PM',
        action: 'Identified top supplier candidates',
        result: 'Success',
      },
    ],
  },
  {
    id: 'agent-003',
    requestNumber: 'REQ-2026-00140',
    product: 'Copper Wire (12 AWG)',
    productCategory: 'Electrical',
    quantity: 1000,
    estimatedValue: 4200,
    status: 'completed',
    currentStatusText: 'Purchase order generated',
    progress: 100,
    startedAt: '2026-01-21T10:00:00Z',
    completedAt: '2026-01-21T11:45:00Z',
    leadTimeDays: 5,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Requirements',
        description:
          'Analyzing purchase requirements and identifying potential suppliers',
        status: 'completed',
        startedAt: '2026-01-21T10:00:00Z',
        completedAt: '2026-01-21T10:02:00Z',
        details: {
          type: 'analysis',
          data: {
            vendorsEvaluated: 5,
            criteriaUsed: ['price', 'quality', 'lead_time'],
            topCandidates: ['ElectroCorp', 'WireWorks', 'CopperKing'],
          },
        },
      },
      {
        id: 'communicate',
        name: 'Communicate with Suppliers',
        description:
          'Sending RFQs, negotiating prices, and confirming delivery timelines',
        status: 'completed',
        startedAt: '2026-01-21T10:02:00Z',
        completedAt: '2026-01-21T10:30:00Z',
        details: {
          type: 'communication',
          data: {
            rfqsSent: 3,
            responsesReceived: 3,
            selectedSupplier: 'ElectroCorp',
            negotiatedDiscount: 8,
          },
        },
      },
      {
        id: 'approval',
        name: 'Human Approval',
        description: 'Awaiting approval from authorized personnel',
        status: 'completed',
        startedAt: '2026-01-21T10:30:00Z',
        completedAt: '2026-01-21T11:15:00Z',
        details: {
          type: 'approval',
          data: {
            approver: 'Sarah Johnson',
            approvedAt: '2026-01-21T11:15:00Z',
          },
        },
      },
      {
        id: 'generate',
        name: 'Generate Purchase Order',
        description:
          'Creating and sending the purchase order to the selected supplier',
        status: 'completed',
        startedAt: '2026-01-21T11:15:00Z',
        completedAt: '2026-01-21T11:45:00Z',
        details: {
          type: 'generation',
          data: {
            poNumber: 'PO-2026-00847',
            sentTo: 'ElectroCorp',
            totalValue: 3864,
          },
        },
      },
    ],
    recentActivity: [
      {
        date: 'Jan 21, 2026',
        time: '11:45 AM',
        action: 'Generated PO-2026-00847',
        result: 'Success',
      },
      {
        date: 'Jan 21, 2026',
        time: '11:15 AM',
        action: 'Approved by Sarah Johnson',
        result: 'Success',
      },
      {
        date: 'Jan 21, 2026',
        time: '10:30 AM',
        action: 'Negotiated 8% discount',
        result: 'Success',
        savings: 336,
      },
    ],
    savings: 336,
    assignedApprover: 'Sarah Johnson',
    supplierSelected: 'ElectroCorp',
  },
  {
    id: 'agent-004',
    requestNumber: 'REQ-2026-00141',
    product: 'Hydraulic Pumps',
    productCategory: 'Equipment',
    quantity: 10,
    estimatedValue: 25000,
    status: 'waiting_approval',
    currentStatusText: 'Waiting for VP approval (high value)',
    progress: 70,
    startedAt: '2026-01-21T09:00:00Z',
    leadTimeDays: 21,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Requirements',
        description:
          'Analyzing purchase requirements and identifying potential suppliers',
        status: 'completed',
        startedAt: '2026-01-21T09:00:00Z',
        completedAt: '2026-01-21T09:05:00Z',
        details: {
          type: 'analysis',
          data: {
            vendorsEvaluated: 4,
            criteriaUsed: ['price', 'warranty', 'certification', 'support'],
            topCandidates: [
              'HydroTech Systems',
              'PumpMaster',
              'FluidPower Inc',
            ],
            specialRequirements: 'ISO 9001 certification required',
          },
        },
      },
      {
        id: 'communicate',
        name: 'Communicate with Suppliers',
        description:
          'Sending RFQs, negotiating prices, and confirming delivery timelines',
        status: 'completed',
        startedAt: '2026-01-21T09:05:00Z',
        completedAt: '2026-01-21T09:45:00Z',
        details: {
          type: 'communication',
          data: {
            rfqsSent: 3,
            responsesReceived: 3,
            selectedSupplier: 'HydroTech Systems',
            negotiatedDiscount: 15,
            warrantyExtended: '3 years',
          },
        },
      },
      {
        id: 'approval',
        name: 'Human Approval',
        description: 'Awaiting approval from authorized personnel',
        status: 'running',
        startedAt: '2026-01-21T09:45:00Z',
        details: {
          type: 'approval',
          data: {
            approver: 'David Thompson',
            approverRole: 'VP of Operations',
            approvalThreshold: '$20,000',
            escalationReason: 'High value purchase',
          },
        },
      },
      {
        id: 'generate',
        name: 'Generate Purchase Order',
        description:
          'Creating and sending the purchase order to the selected supplier',
        status: 'pending',
      },
    ],
    recentActivity: [
      {
        date: 'Jan 21, 2026',
        time: '9:45 AM',
        action: 'Escalated to VP for approval (>$20K)',
        result: 'Pending Approval',
      },
      {
        date: 'Jan 21, 2026',
        time: '9:40 AM',
        action: 'Secured 15% volume discount',
        result: 'Success',
        savings: 3750,
      },
      {
        date: 'Jan 21, 2026',
        time: '9:05 AM',
        action: 'Contacted 3 certified vendors',
        result: 'Success',
      },
    ],
    savings: 3750,
    assignedApprover: 'David Thompson (VP)',
    supplierSelected: 'HydroTech Systems',
  },
  {
    id: 'agent-005',
    requestNumber: 'REQ-2026-00144',
    product: 'Safety Gloves (Box)',
    productCategory: 'Safety Equipment',
    quantity: 50,
    estimatedValue: 750,
    status: 'running',
    currentStatusText: 'Analyzing supplier options',
    progress: 20,
    startedAt: '2026-01-21T15:30:00Z',
    leadTimeDays: 3,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Requirements',
        description:
          'Analyzing purchase requirements and identifying potential suppliers',
        status: 'running',
        startedAt: '2026-01-21T15:30:00Z',
        details: {
          type: 'analysis',
          data: {
            vendorsBeingEvaluated: 4,
            checkingInventory: true,
            checkingPreferredSuppliers: true,
          },
        },
      },
      {
        id: 'communicate',
        name: 'Communicate with Suppliers',
        description:
          'Sending RFQs, negotiating prices, and confirming delivery timelines',
        status: 'pending',
      },
      {
        id: 'approval',
        name: 'Human Approval',
        description: 'Awaiting approval from authorized personnel',
        status: 'pending',
      },
      {
        id: 'generate',
        name: 'Generate Purchase Order',
        description:
          'Creating and sending the purchase order to the selected supplier',
        status: 'pending',
      },
    ],
    recentActivity: [
      {
        date: 'Jan 21, 2026',
        time: '3:32 PM',
        action: 'Checking inventory levels',
        result: 'In Progress',
      },
      {
        date: 'Jan 21, 2026',
        time: '3:30 PM',
        action: 'Started procurement process',
        result: 'Success',
      },
    ],
  },
  {
    id: 'agent-006',
    requestNumber: 'REQ-2026-00139',
    product: 'Aluminum Sheets (4x8)',
    productCategory: 'Raw Materials',
    quantity: 100,
    estimatedValue: 6800,
    status: 'failed',
    currentStatusText: 'Failed - No suppliers available',
    progress: 35,
    startedAt: '2026-01-20T14:00:00Z',
    leadTimeDays: 10,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Requirements',
        description:
          'Analyzing purchase requirements and identifying potential suppliers',
        status: 'completed',
        startedAt: '2026-01-20T14:00:00Z',
        completedAt: '2026-01-20T14:03:00Z',
        details: {
          type: 'analysis',
          data: {
            vendorsEvaluated: 3,
            criteriaUsed: ['price', 'availability', 'lead_time'],
          },
        },
      },
      {
        id: 'communicate',
        name: 'Communicate with Suppliers',
        description:
          'Sending RFQs, negotiating prices, and confirming delivery timelines',
        status: 'failed',
        startedAt: '2026-01-20T14:03:00Z',
        completedAt: '2026-01-20T14:30:00Z',
        details: {
          type: 'communication',
          data: {
            rfqsSent: 3,
            responsesReceived: 3,
            failureReason:
              'All suppliers exceeded budget by >30% or could not meet timeline',
            quotesReceived: [
              { supplier: 'AluminumPro', quote: 9200, overBudget: '35%' },
              { supplier: 'MetalSource', quote: 8900, overBudget: '31%' },
              {
                supplier: 'SheetMetal Inc',
                leadTime: '45 days',
                issue: 'Timeline exceeded',
              },
            ],
          },
        },
      },
      {
        id: 'approval',
        name: 'Human Approval',
        description: 'Awaiting approval from authorized personnel',
        status: 'pending',
      },
      {
        id: 'generate',
        name: 'Generate Purchase Order',
        description:
          'Creating and sending the purchase order to the selected supplier',
        status: 'pending',
      },
    ],
    recentActivity: [
      {
        date: 'Jan 20, 2026',
        time: '2:30 PM',
        action: 'No suppliers could meet timeline requirements',
        result: 'Action Required',
      },
      {
        date: 'Jan 20, 2026',
        time: '2:15 PM',
        action: 'All quotes exceeded budget by >30%',
        result: 'Action Required',
      },
    ],
  },
];

// Mutable store
let buyingAgents: BuyingAgent[] = [...initialBuyingAgents];

// Counter for generating unique IDs
let agentCounter = 100;

// Get all agents
export function getBuyingAgents(): BuyingAgent[] {
  return buyingAgents;
}

// Get a single agent by ID
export function getBuyingAgentById(id: string): BuyingAgent | undefined {
  return buyingAgents.find((a) => a.id === id);
}

// Create a new buying agent and return its ID
// Agent is created as if it was triggered 24 hours ago and is now in the Communication phase
export function createBuyingAgent(params: {
  product: string;
  productCategory: string;
  quantity: number;
  estimatedValue: number;
  leadTimeDays: number;
  sku?: string;
}): string {
  agentCounter++;
  const requestNumber = `REQ-2026-${String(agentCounter).padStart(5, '0')}`;
  const agentId = `agent-${Date.now()}`;

  // Started 24 hours ago
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const startedAt = yesterday.toISOString();

  // Analyze completed 23 hours ago (took about 1 hour)
  const analyzeCompletedAt = new Date(
    yesterday.getTime() + 60 * 60 * 1000,
  ).toISOString();

  // Communication started right after analyze completed
  const communicationStartedAt = analyzeCompletedAt;

  // Email sent 22 hours ago
  const emailSentTime = new Date(
    yesterday.getTime() + 2 * 60 * 60 * 1000,
  ).toISOString();

  // MetalWorks replied 20 hours ago
  const metalWorksReplyTime = new Date(
    yesterday.getTime() + 4 * 60 * 60 * 1000,
  ).toISOString();

  // Call with Silicon Valley Materials happened 18 hours ago
  const callTime = new Date(
    yesterday.getTime() + 6 * 60 * 60 * 1000,
  ).toISOString();

  const newAgent: BuyingAgent = {
    id: agentId,
    requestNumber,
    product: params.product,
    productCategory: params.productCategory,
    quantity: params.quantity,
    estimatedValue: params.estimatedValue,
    status: 'running',
    currentStatusText: 'Communicating with suppliers',
    progress: 55,
    startedAt,
    leadTimeDays: params.leadTimeDays,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Requirements',
        description:
          'Analyzed purchase requirements and identified potential suppliers',
        status: 'completed',
        startedAt,
        completedAt: analyzeCompletedAt,
        details: {
          type: 'analysis',
          data: {
            vendorsEvaluated: 5,
            criteriaUsed: ['price', 'quality', 'lead_time', 'reliability'],
            topCandidates: [
              'MetalWorks Inc',
              'Silicon Valley Materials',
              'Industrial Metals',
            ],
            budgetCheck: 'passed',
            inventoryCheck: 'low_stock_confirmed',
          },
        },
      },
      {
        id: 'communicate',
        name: 'Communicate with Suppliers',
        description:
          'Negotiating prices and confirming delivery timelines with vendors',
        status: 'running',
        startedAt: communicationStartedAt,
        details: {
          type: 'communication',
          data: {
            rfqsSent: 3,
            responsesReceived: 2,
            awaitingResponse: ['Industrial Metals'],
            activeCommunications: [
              {
                id: 'comm-1',
                supplier: 'MetalWorks Inc',
                type: 'email' as const,
                status: 'responded' as const,
                subject: `RFQ ${requestNumber} - ${params.product}`,
                sentAt: emailSentTime,
                lastActivity: 'Price approved - Ready to proceed',
                summary:
                  'Quote approved at target price, 2 days ahead of schedule',
                quotedPrice: params.estimatedValue,
                quotedLeadTime: `${params.leadTimeDays - 2} days`,
                emailThread: [
                  {
                    id: 'msg-1',
                    from: 'procurement@gently.ai',
                    to: 'sales@metalworks-inc.com',
                    sentAt: emailSentTime,
                    body: `Dear MetalWorks Inc Sales Team,

We are reaching out to request a quote for the following materials:

**Product:** ${params.product}
**Quantity:** ${params.quantity.toLocaleString()} units
**Required Delivery:** Within ${params.leadTimeDays} days

We have been impressed with your track record for quality materials and competitive pricing. As a growing manufacturing company, we are looking to establish a long-term supplier relationship.

Please provide your best pricing along with:
- Lead time confirmation
- Any volume discount opportunities
- Payment terms
- Quality certifications

We look forward to your prompt response.

Best regards,
AI Procurement Agent
Gently ERP System`,
                    attachments: [
                      'product_specifications.pdf',
                      'quality_requirements.pdf',
                    ],
                  },
                  {
                    id: 'msg-2',
                    from: 'sarah.chen@metalworks-inc.com',
                    to: 'procurement@gently.ai',
                    sentAt: metalWorksReplyTime,
                    body: `Hi there,

Thank you for your RFQ. We're pleased to confirm we can fulfill this order!

**Approved Quote:**
- Product: ${params.product}
- Unit Price: $${(params.estimatedValue / params.quantity).toFixed(2)}/unit
- Quantity: ${params.quantity.toLocaleString()} units
- **Total: $${params.estimatedValue.toLocaleString()}**
- Lead Time: ${params.leadTimeDays - 2} days (2 days ahead of your requirement!)

✅ **Price Approved** - We've matched your target budget and can proceed immediately upon your approval.

We also want to mention that we have additional inventory available if you need to scale up this order in the future.

Please let us know how you'd like to proceed!

Best regards,
Sarah Chen
Senior Sales Representative
MetalWorks Inc
📞 (555) 234-5678`,
                    isReply: true,
                  },
                ],
              },
              {
                id: 'comm-2',
                supplier: 'Silicon Valley Materials',
                type: 'call' as const,
                status: 'completed' as const,
                subject: 'Pricing Discussion Call',
                sentAt: callTime,
                lastActivity: 'Call completed - Recording available',
                summary:
                  'Bulk rate offered at $47/unit, 14-day lead time confirmed',
                quotedPrice: 47 * params.quantity,
                quotedLeadTime: '14 days',
                callRecording: {
                  duration: '2:17',
                  recordingUrl: '/recordings/svm-call-001.mp3',
                  summary:
                    'Discussed pricing for silicon wafers. They offered $47/unit for bulk orders over 2,000 units. Confirmed 14-day lead time. Will send formal quote via email.',
                  transcript: `[00:00] AI Agent: Good morning, this is the procurement AI from Gently ERP calling regarding our RFQ for silicon wafers.

[00:08] SVM Rep: Hi there! Yes, I have your request in front of me. You're looking for ${params.quantity.toLocaleString()} units, correct?

[00:15] AI Agent: That's correct. We need delivery within ${params.leadTimeDays} days. Can you confirm availability and your best pricing?

[00:25] SVM Rep: Absolutely. For that quantity, we can offer $47 per unit. That's our bulk rate for orders over 2,000 units.

[00:35] AI Agent: That's within our budget. What about lead time?

[00:40] SVM Rep: We can do 14 days, no problem. We have good inventory levels right now.

[00:48] AI Agent: Excellent. Can you send over a formal quote with those terms?

[00:55] SVM Rep: Of course, I'll have that to you by end of day. Is there anything else you need?

[01:02] AI Agent: That covers everything. Thank you for your time.

[01:08] SVM Rep: Thank you! Talk soon.`,
                },
              },
              {
                id: 'comm-3',
                supplier: 'Industrial Metals',
                type: 'email' as const,
                status: 'awaiting_response' as const,
                subject: `RFQ ${requestNumber} - ${params.product}`,
                sentAt: emailSentTime,
                lastActivity: 'Sent 22 hours ago - No response',
                summary: 'No response from supplier',
                supplierPhone: '(555) 789-0123',
                emailContent: {
                  to: 'sales@industrialmetals.com',
                  from: 'procurement@gently.ai',
                  body: `Dear Industrial Metals Team,

We are requesting a competitive quote for:

**Product:** ${params.product}
**Quantity:** ${params.quantity.toLocaleString()} units
**Required Delivery:** Within ${params.leadTimeDays} days

Please provide your best pricing and availability.

Best regards,
AI Procurement Agent
Gently ERP System`,
                  attachments: ['rfq_details.pdf'],
                },
              },
            ],
          },
        },
      },
      {
        id: 'approval',
        name: 'Human Approval',
        description: 'Review supplier recommendation and create purchase order',
        status: 'pending',
      },
      {
        id: 'generate',
        name: 'Generate Purchase Order',
        description:
          'Creating and sending the purchase order to the selected supplier',
        status: 'pending',
      },
    ],
    recentActivity: [
      {
        date: 'Today',
        time: '9:00 AM',
        action: 'Waiting for response from 2 suppliers',
        result: 'In Progress',
      },
      {
        date: 'Yesterday',
        time: '4:00 PM',
        action: 'Received price approval from MetalWorks Inc',
        result: 'Success',
      },
      {
        date: 'Yesterday',
        time: '2:00 PM',
        action: 'Completed call with Silicon Valley Materials',
        result: 'Success',
      },
      {
        date: 'Yesterday',
        time: '10:00 AM',
        action: 'Sent RFQs to 3 suppliers',
        result: 'Success',
      },
      {
        date: 'Yesterday',
        time: '9:00 AM',
        action: 'Started procurement process',
        result: 'In Progress',
      },
    ],
  };

  // Add to beginning of array so it appears first
  buyingAgents = [newAgent, ...buyingAgents];

  return agentId;
}

// Progress agent to step 2 (Communicate with Suppliers)
export function progressAgentToStep2(agentId: string): boolean {
  const agentIndex = buyingAgents.findIndex((a) => a.id === agentId);
  if (agentIndex === -1) return false;

  const agent = buyingAgents[agentIndex];
  const now = new Date().toISOString();

  // Update the agent
  const updatedAgent: BuyingAgent = {
    ...agent,
    progress: 45,
    currentStatusText: 'Communicating with suppliers',
    steps: agent.steps.map((step) => {
      if (step.id === 'analyze') {
        return {
          ...step,
          status: 'completed' as const,
          completedAt: now,
          details: {
            type: 'analysis' as const,
            data: {
              vendorsEvaluated: 8,
              criteriaUsed: ['price', 'quality', 'lead_time', 'reliability'],
              topCandidates: ['MetalWorks Inc', 'SteelCo', 'Industrial Metals'],
              budgetCheck: 'passed',
              inventoryCheck: 'low_stock_confirmed',
            },
          },
        };
      }
      if (step.id === 'communicate') {
        return {
          ...step,
          status: 'running' as const,
          startedAt: now,
          description:
            'Actively communicating with suppliers to negotiate best prices and terms',
          details: {
            type: 'communication' as const,
            data: {
              rfqsSent: 3,
              responsesReceived: 1,
              awaitingResponse: ['SteelCo', 'Industrial Metals'],
              activeCommunications: [
                {
                  id: 'comm-1',
                  supplier: 'MetalWorks Inc',
                  type: 'email' as const,
                  status: 'responded' as const,
                  subject: 'RFQ #2026-0142 - Steel Rods (Grade A)',
                  sentAt: now,
                  lastActivity: 'Replied 2 min ago',
                  summary: 'Quoted $24.50/unit with 12% bulk discount offer',
                  quotedPrice: 12250,
                  quotedLeadTime: '12 days',
                  volumeDiscountOffer: {
                    product: 'Industrial Bearings',
                    sku: 'BRG-1001',
                    discountPercent: 12,
                    minQuantity: 200,
                  },
                  emailThread: [
                    {
                      id: 'msg-1',
                      from: 'procurement@gently.ai',
                      to: 'sales@metalworks-inc.com',
                      sentAt: new Date(
                        Date.now() - 10 * 60 * 1000,
                      ).toISOString(), // 10 min ago
                      body: `Dear MetalWorks Inc Sales Team,

We are reaching out to request a quote for the following materials:

**Product:** Steel Rods (Grade A)
**Quantity:** 500 units
**Required Delivery:** Within 14 days

We have been impressed with your track record for quality materials and competitive pricing. As a growing manufacturing company, we are looking to establish a long-term supplier relationship.

Please provide your best pricing along with:
- Lead time confirmation
- Any volume discount opportunities
- Payment terms
- Quality certifications

We look forward to your prompt response.

Best regards,
AI Procurement Agent
Gently ERP System`,
                      attachments: [
                        'product_specifications.pdf',
                        'quality_requirements.pdf',
                      ],
                    },
                    {
                      id: 'msg-2',
                      from: 'sarah.chen@metalworks-inc.com',
                      to: 'procurement@gently.ai',
                      sentAt: new Date(
                        Date.now() - 2 * 60 * 1000,
                      ).toISOString(), // 2 min ago
                      body: `Hi there,

Thank you for reaching out! We'd be happy to provide a quote for the Steel Rods (Grade A).

**Our Quote:**
- Unit Price: $24.50/unit
- Quantity: 500 units
- Total: $12,250
- Lead Time: 12 days (we can expedite!)

**🎉 Special Offer:**
I noticed you might also need Industrial Bearings based on your company profile. If you add Industrial Bearings (minimum 200 units) to this order, we can offer you **12% off your entire order**!

That would bring your Steel Rods down to $21.56/unit - a savings of $1,470!

Here's what the combined order would look like:
- Steel Rods (500 units): $10,780 (after 12% discount)
- Industrial Bearings (200 units): $7,040 (after 12% discount)
- **Total Savings: $2,420**

Let me know if you'd like to proceed with the combined order for maximum savings!

Best regards,
Sarah Chen
Senior Sales Representative
MetalWorks Inc
📞 (555) 234-5678`,
                      isReply: true,
                    },
                  ],
                },
                {
                  id: 'comm-2',
                  supplier: 'SteelCo',
                  type: 'email' as const,
                  status: 'sent' as const,
                  subject: 'RFQ #2026-0142 - Steel Rods (Grade A)',
                  sentAt: now,
                  lastActivity: 'Sent just now',
                  summary: 'RFQ sent, awaiting supplier response',
                  emailContent: {
                    to: 'quotes@steelco.com',
                    from: 'procurement@gently.ai',
                    body: `Dear SteelCo Quotations Team,

I am writing to request a competitive quote for the following order:

**Product:** Steel Rods (Grade A)
**Quantity:** 500 units
**Delivery Timeline:** 14 days

Your company was recommended by our engineering team for your consistent quality standards. We are evaluating multiple suppliers for this order and would appreciate your most competitive offer.

Please include in your response:
- Unit pricing and total cost
- Delivery schedule
- Warranty information
- Any applicable bulk discounts

Thank you for your time. We aim to make a decision within the next 48 hours.

Best regards,
AI Procurement Agent
Gently ERP System`,
                    attachments: ['rfq_details.pdf'],
                  },
                },
                {
                  id: 'comm-3',
                  supplier: 'Industrial Metals',
                  type: 'call' as const,
                  status: 'in_progress' as const,
                  lastActivity: 'On call with sales rep',
                  summary: 'Currently negotiating pricing and terms',
                },
              ],
            },
          },
        };
      }
      return step;
    }),
    recentActivity: [
      {
        date: 'Just now',
        time: '',
        action: 'Started call with Industrial Metals',
        result: 'In Progress',
      },
      {
        date: 'Just now',
        time: '',
        action: 'Sent RFQ to SteelCo via email',
        result: 'Success',
      },
      {
        date: 'Just now',
        time: '',
        action: 'Sent RFQ to MetalWorks Inc via email',
        result: 'Success',
      },
      {
        date: 'Just now',
        time: '',
        action: 'Completed requirements analysis',
        result: 'Success',
      },
      ...agent.recentActivity,
    ],
  };

  buyingAgents[agentIndex] = updatedAgent;
  return true;
}

// Progress agent to step 3 (Human Approval) - called after supplier communication ends
export function progressAgentToStep3(agentId: string): boolean {
  const agentIndex = buyingAgents.findIndex((a) => a.id === agentId);
  if (agentIndex === -1) return false;

  const agent = buyingAgents[agentIndex];
  const now = new Date().toISOString();

  // Update the agent
  const updatedAgent: BuyingAgent = {
    ...agent,
    status: 'waiting_approval',
    progress: 75,
    currentStatusText: 'Review AI recommendation',
    steps: agent.steps.map((step) => {
      if (step.id === 'communicate') {
        // Get existing communication details and update Industrial Metals status
        const existingDetails = step.details?.data as
          | CommunicationData
          | undefined;
        const updatedCommunications =
          existingDetails?.activeCommunications?.map((comm) => {
            if (comm.supplier === 'Industrial Metals') {
              // Mark Industrial Metals as completed and remove supplierPhone
              const { supplierPhone, ...rest } = comm;
              return {
                ...rest,
                status: 'completed' as const,
                summary: 'No response from supplier',
              };
            }
            return comm;
          });

        return {
          ...step,
          status: 'completed' as const,
          completedAt: now,
          details:
            existingDetails && step.details
              ? {
                  type: step.details.type,
                  data: {
                    ...existingDetails,
                    awaitingResponse: [], // Clear awaiting response
                    activeCommunications: updatedCommunications,
                  },
                }
              : step.details,
        };
      }
      if (step.id === 'approval') {
        return {
          ...step,
          status: 'running' as const,
          startedAt: now,
          description:
            'Review supplier recommendation and create purchase order',
          details: {
            type: 'approval' as const,
            data: {
              requestedAt: now,
              recommendedSupplier: 'MetalWorks Inc',
              unitPrice: 45,
              totalPrice: 112500,
              quantity: 2500,
              leadTime: '12 days',
              recommendation:
                'Based on supplier communication, MetalWorks Inc offers the best combination of competitive pricing at $45/unit and a reliable 12-day lead time.',
            },
          },
        };
      }
      return step;
    }),
  };

  buyingAgents[agentIndex] = updatedAgent;
  return true;
}

// Check if agent is newly created (within last minute)
export function isNewlyCreatedAgent(agentId: string): boolean {
  const agent = getBuyingAgentById(agentId);
  if (!agent) return false;

  const startedAt = new Date(agent.startedAt);
  const now = new Date();
  const diffMs = now.getTime() - startedAt.getTime();
  const diffSeconds = diffMs / 1000;

  // Consider "newly created" if started within the last 60 seconds
  return diffSeconds < 60;
}

// Reset to initial state (useful for testing)
export function resetBuyingAgents(): void {
  buyingAgents = [...initialBuyingAgents];
  agentCounter = 100;
}
